import { persistOperationalShift } from "./core/finance/financialService";
import { partitionFinancialData } from "./core/finance/financialBoundary";
/*
 * =========================================================
 * SHIFTBRIDGE — CANONICAL FIRESTORE SHIFT PERSISTENCE
 * =========================================================
 *
 * File:
 *   src/firestoreShifts.js
 *
 * Purpose:
 *   Firestore persistence boundary for ShiftBridge shifts.
 *
 * Architecture:
 *
 *   UI
 *    ↓
 *   core/services/shiftService.js
 *    ↓
 *   firestoreShifts.js
 *    ↓
 *   Firestore
 *
 * Business screens should not write directly to /shifts.
 */

import {
  Timestamp,
  addDoc,
  arrayUnion,
  arrayRemove,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import {
  auth,
  db,
} from "./firebase";
import {
  assertShiftActivation,
} from "./core/shifts/shiftValidation";

/* =========================================================
   NORMALIZATION
   ========================================================= */

function normalizeId(value) {
  return String(
    value || ""
  )
    .trim()
    .toLowerCase();
}

function normalizeUid(value) {
  return String(
    value || ""
  ).trim();
}

function normalizeText(value) {
  return String(
    value || ""
  ).trim();
}

function normalizeEmail(value) {
  return String(
    value || ""
  )
    .trim()
    .toLowerCase();
}

export function normalizeShiftRole(
  value
) {
  const role =
    String(value || "")
      .trim()
      .toUpperCase();

  const aliases = {
    CNA: "CNA",
    "CERTIFIED NURSING ASSISTANT":
      "CNA",

    LVN: "LVN",
    LPN: "LVN",
    "LICENSED VOCATIONAL NURSE":
      "LVN",
    "LICENSED PRACTICAL NURSE":
      "LVN",

    RN: "RN",
    "REGISTERED NURSE":
      "RN",

    MED: "MED",
    "MED AIDE": "MED",
    "MEDICATION AIDE": "MED",
    "MEDICATION ASSISTANT":
      "MED",

    CARE: "CARE",
    CAREGIVER: "CARE",
  };

  return aliases[role] || role;
}

function toDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (
    typeof value?.toDate ===
    "function"
  ) {
    return value.toDate();
  }

  const result =
    new Date(value);

  return Number.isFinite(
    result.getTime()
  )
    ? result
    : null;
}

function toTimestamp(value) {
  const date =
    toDate(value);

  return date
    ? Timestamp.fromDate(date)
    : null;
}

function currentUid() {
  return (
    auth.currentUser?.uid ||
    ""
  );
}

/* =========================================================
   SHIFT READ HELPERS
   ========================================================= */

async function getShiftOrThrow(
  shiftId
) {
  const id =
    normalizeUid(shiftId);

  if (!id) {
    throw new Error(
      "Missing shiftId"
    );
  }

  const ref =
    doc(
      db,
      "shifts",
      id
    );

  const snap =
    await getDoc(ref);

  if (!snap.exists()) {
    throw new Error(
      "Shift not found"
    );
  }

  return {
    ref,
    id: snap.id,
    data: snap.data(),
  };
}

function assignedUidFromShift(
  shift = {}
) {
  return normalizeUid(
    shift?.assignedWorkerUid ||
      shift?.assignedWorkerId ||
      shift?.workerUid ||
      shift?.workerId ||
      (
        Array.isArray(
          shift?.assignedWorkerUids
        )
          ? shift
              .assignedWorkerUids[0]
          : ""
      ) ||
      ""
  );
}

/* =========================================================
   WORKER / OVERLAP VALIDATION
   ========================================================= */

async function getWorkerOrThrow(
  workerUid
) {
  const uid =
    normalizeUid(workerUid);

  if (!uid) {
    throw new Error(
      "Missing worker UID"
    );
  }

  const snap =
    await getDoc(
      doc(
        db,
        "users",
        uid
      )
    );

  if (!snap.exists()) {
    throw new Error(
      "Worker not found"
    );
  }

  return {
    uid,
    ...snap.data(),
  };
}

function workerIsActive(
  worker = {}
) {
  const status =
    normalizeId(
      worker?.status ||
        worker?.workerStatus
    );

  if (
    worker?.disabled === true ||
    worker?.archived === true
  ) {
    return false;
  }

  if (!status) {
    return true;
  }

  return ![
    "disabled",
    "inactive",
    "archived",
    "rejected",
    "suspended",
  ].includes(status);
}

function workerMatchesRole(
  worker,
  shiftRole
) {
  const requiredRole =
    normalizeShiftRole(
      shiftRole
    );

  if (!requiredRole) {
    return true;
  }

  const candidates = [
    worker?.position,
    worker?.primaryRole,
    worker?.credential,
    worker?.licenseType,
    worker?.jobTitle,
    worker?.workerRole,
  ];

  if (
    Array.isArray(
      worker?.positions
    )
  ) {
    candidates.push(
      ...worker.positions
    );
  }

  const normalized =
    candidates
      .filter(Boolean)
      .map(
        normalizeShiftRole
      );

  /*
   * During migration some worker docs
   * may not yet carry a clean credential
   * field. Do not reject solely because
   * the profile is incomplete.
   */
  if (
    normalized.length === 0
  ) {
    return true;
  }

  return normalized.includes(
    requiredRole
  );
}

function rangesOverlap(
  startA,
  endA,
  startB,
  endB
) {
  return (
    startA < endB &&
    startB < endA
  );
}

export async function assertNoOverlappingAssignedShift(
  workerUid,
  startTime,
  endTime,
  excludeShiftId = "",
  tenantId = "",
  facilityId = ""
) {
  const uid =
    normalizeUid(
      workerUid
    );

  const cleanTenantId =
    normalizeId(
      tenantId
    );

  const cleanFacilityId =
    normalizeId(
      facilityId
    );

  const start =
    toDate(
      startTime
    );

  const end =
    toDate(
      endTime
    );

  if (
    !uid ||
    !start ||
    !end
  ) {
    return true;
  }

  if (!cleanTenantId) {
    throw new Error(
      "Missing tenantId for overlap check."
    );
  }

  // Every query must carry a facility scope. A worker accepting a direct
  // offer checks all of their authorized facilities, so cross-facility
  // conflicts remain visible without a tenant-wide query.
  let facilityIds = cleanFacilityId ? [cleanFacilityId] : [];
  if (!cleanFacilityId && uid === currentUid()) {
    const access = await getDoc(doc(db, "accessProfiles", uid));
    const data = access.data() || {};
    if (normalizeId(data.tenantId) !== cleanTenantId) {
      throw new Error("Worker tenant does not match this shift.");
    }
    facilityIds = [...new Set([
      data.facilityId, data.primaryFacilityId,
      ...(Array.isArray(data.facilityIds) ? data.facilityIds : []),
      ...(Array.isArray(data.allowedFacilityIds) ? data.allowedFacilityIds : []),
    ].map(normalizeId).filter(value => value && value !== "all"))];
  }
  if (!facilityIds.length) {
    throw new Error("A permitted facility is required for the overlap check.");
  }

  const constraints = [
    where(
      "tenantId",
      "==",
      cleanTenantId
    ),

    where(
      "assignedWorkerUid",
      "==",
      uid
    ),
  ];

  const snapshots = await Promise.all(facilityIds.map(id => getDocs(query(
    collection(db, "shifts"), ...constraints, where("facilityId", "==", id)
  ))));

  for (
    const existingDoc of
    snapshots.flatMap(snapshot => snapshot.docs)
  ) {
    if (
      existingDoc.id ===
      excludeShiftId
    ) {
      continue;
    }

    const shift =
      existingDoc.data();

    const status =
      normalizeId(
        shift?.status ||
          shift?.lifecycleStatus
      );

    if (
      ![
        "assigned",
        "activated",
        "tracking",
        "arrived",
      ].includes(status)
    ) {
      continue;
    }

    const existingStart =
      toDate(
        shift?.startTime ||
          shift?.start
      );

    const existingEnd =
      toDate(
        shift?.endTime ||
          shift?.end
      );

    if (
      !existingStart ||
      !existingEnd
    ) {
      continue;
    }

    if (
      rangesOverlap(
        start.getTime(),
        end.getTime(),
        existingStart.getTime(),
        existingEnd.getTime()
      )
    ) {
      throw new Error(
        "Worker already has an overlapping assigned shift."
      );
    }
  }

  return true;
}

/* =========================================================
   CREATE SHIFT
   ========================================================= */

export async function createShift(
  shiftData = {}
) {
  const {
    tenantId,
    tenantName,
    tenantType,

    facilityId,
    facilityName,

    role,

    status:
      providedStatus,

    lifecycleStatus:
      providedLifecycleStatus,

    startTime,
    endTime,

    start,
    end,

    source = "manual",

    mode = "pool",
    assignMode = "",

    assignedWorkerUid = "",
    assignedWorkerUids = [],
    assignedWorkerId = "",

    workerUid = "",
    workerId = "",

    assignedWorkerName = "",
    assignedWorkerEmail = "",

    offeredWorkerUids = [],

    createdBy = "",
    createdByUid = "",
    createdByEmail = "",
    createdByRole = "",

    billRate = 0,
    rates = null,

    ...extra
  } = shiftData;

  const cleanTenantId =
    normalizeId(
      tenantId ||
        "dogebridge"
    );

  const cleanFacilityId =
    normalizeId(
      facilityId
    );

  const cleanRole =
    normalizeShiftRole(
      role
    );

  if (!cleanTenantId) {
    throw new Error(
      "Missing tenantId"
    );
  }

  if (!cleanFacilityId) {
    throw new Error(
      "Missing facilityId"
    );
  }

  if (!cleanRole) {
    throw new Error(
      "Missing role"
    );
  }

  const cleanMode =
    normalizeId(
      mode ||
        assignMode ||
        "pool"
    );

  const cleanSource =
    normalizeId(
      source ||
        "manual"
    );

  const effectiveStart =
    startTime ||
    start;

  const effectiveEnd =
    endTime ||
    end;

  const startDate =
    toDate(
      effectiveStart
    );

  const endDate =
    toDate(
      effectiveEnd
    );

  const isFlexibleEmergency =
    shiftData
      ?.isFlexibleTime ===
      true ||
    shiftData?.isFlexible ===
      true ||
    shiftData
      ?.flexibleEmergency ===
      true ||
    cleanSource.includes(
      "flexible"
    ) ||
    cleanSource.includes(
      "emergency"
    ) ||
    cleanMode.includes(
      "flexible"
    );

  const hasProvidedTime =
    !!startDate &&
    !!endDate;

  const shouldRequireTime =
    isFlexibleEmergency &&
    !hasProvidedTime;

  if (
    !isFlexibleEmergency &&
    !hasProvidedTime
  ) {
    throw new Error(
      "Missing required shift fields"
    );
  }

  const st =
    startDate ||
    new Date();

  const en =
    endDate ||
    new Date(
      st.getTime() +
        60 *
          60 *
          1000
    );

  if (
    !Number.isFinite(
      st.getTime()
    ) ||
    !Number.isFinite(
      en.getTime()
    )
  ) {
    throw new Error(
      "Shift time invalid"
    );
  }

  if (
    en.getTime() <=
    st.getTime()
  ) {
    if (
      isFlexibleEmergency
    ) {
      en.setTime(
        st.getTime() +
          60 *
            60 *
            1000
      );
    } else {
      throw new Error(
        "Shift end time must be after start time"
      );
    }
  }

  const cleanAssignedUid =
    normalizeUid(
      assignedWorkerUid ||
        assignedWorkerId ||
        workerUid ||
        workerId ||
        (
          Array.isArray(
            assignedWorkerUids
          )
            ? assignedWorkerUids[0]
            : ""
        )
    );

  const cleanOffers =
    Array.isArray(
      offeredWorkerUids
    )
      ? [
          ...new Set(
            offeredWorkerUids
              .map(
                normalizeUid
              )
              .filter(
                Boolean
              )
          ),
        ]
      : [];

  const isDirect =
    cleanMode ===
    "direct";

  if (
    isDirect &&
    !cleanAssignedUid &&
    cleanOffers.length === 0
  ) {
    throw new Error(
      "Direct shift requires at least one offered worker."
    );
  }

  const cleanProvidedStatus =
    normalizeId(
      providedStatus
    );

  let initialStatus;

  if (
    shouldRequireTime
  ) {
    initialStatus =
      "time_required";
  } else if (
    cleanAssignedUid
  ) {
    initialStatus =
      "assigned";
  } else if (
    [
      "open",
      "offered",
      "assigned",
      "time_required",
    ].includes(
      cleanProvidedStatus
    )
  ) {
    initialStatus =
      cleanProvidedStatus;
  } else if (
    isDirect
  ) {
    initialStatus =
      "offered";
  } else {
    initialStatus =
      "open";
  }

  const initialLifecycle =
    normalizeId(
      providedLifecycleStatus ||
        initialStatus
    );

  const actorUid =
    normalizeUid(
      createdByUid ||
        createdBy ||
        currentUid()
    );

  const cleanBillRate =
    Number(
      billRate || rates?.billRate || 0
    );



  /*
   * IMPORTANT:
   *
   * This function commits ONE atomic batch.
   * Explicit compensation is stored in a restricted companion document.
   *
   * No audit collection.
   * No facility write.
   * No worker mutation.
   * No hidden follow-up operation.
   */

console.error(
  "CREATE SHIFT — CANONICAL DIAGNOSTIC",
  {
    authUid: currentUid(),
    tenantId: cleanTenantId,
    facilityId: cleanFacilityId,
    role: cleanRole,
    mode: cleanMode,
    status: initialStatus,
    assignedWorkerUid: cleanAssignedUid || null,
    offeredWorkerUids: cleanOffers,
  }
);

  const ref =
    await persistOperationalShift({
        /*
         * Extension fields first.
         *
         * Canonical identity/lifecycle fields
         * below overwrite any accidental
         * duplicates from extra.
         */
        ...extra,
        ...partitionFinancialData(shiftData).financial,

        tenantId:
          cleanTenantId,

        tenantName:
          normalizeText(
            tenantName ||
              shiftData
                ?.tenantName ||
              "DogeBridge Staffing"
          ),

        tenantType:
          normalizeId(
            tenantType ||
              shiftData
                ?.tenantType ||
              "staffing_agency"
          ),

        facilityId:
          cleanFacilityId,

        facilityName:
          normalizeText(
            facilityName ||
              shiftData?.name ||
              cleanFacilityId
          ),

        role:
          cleanRole,

        requiredPosition:
          cleanRole,

        startTime:
          Timestamp.fromDate(
            st
          ),

        endTime:
          Timestamp.fromDate(
            en
          ),

        start:
          Timestamp.fromDate(
            st
          ),

        end:
          Timestamp.fromDate(
            en
          ),

        source:
          cleanSource,

        mode:
          isDirect
            ? "direct"
            : "pool",

        assignMode:
          isDirect
            ? "direct"
            : "pool",

        status:
          initialStatus,

        lifecycleStatus:
          initialLifecycle,

        billRate:
          cleanBillRate,

        createdBy:
          actorUid,

        createdByUid:
          actorUid,

        createdByEmail:
          normalizeEmail(
            createdByEmail
          ),

        createdByRole:
          normalizeId(
            createdByRole
          ),

        isFlexible:
          isFlexibleEmergency,

        isFlexibleTime:
          shouldRequireTime,

        flexibleEmergency:
          isFlexibleEmergency,

        timeRequired:
          shouldRequireTime,

        timeConfirmed:
          !shouldRequireTime,

        assignedWorkerUid:
          cleanAssignedUid ||
          null,

        assignedWorkerUids:
          cleanAssignedUid
            ? [
                cleanAssignedUid,
              ]
            : [],

        assignedWorkerId:
          cleanAssignedUid,

        assignedWorkerName:
          normalizeText(
            assignedWorkerName
          ),

        assignedWorkerEmail:
          normalizeEmail(
            assignedWorkerEmail
          ),

        workerUid:
          cleanAssignedUid,

        workerId:
          cleanAssignedUid,

        assignedAt:
          cleanAssignedUid
            ? serverTimestamp()
            : null,

        assignmentVersion:
          cleanAssignedUid
            ? 1
            : 0,

        assignmentAccepted:
          false,

        acceptedByWorker:
          false,

        acceptedWorkerUid:
          "",

        acceptedAt:
          null,

        workerResponseStatus:
          cleanAssignedUid
            ? "pending"
            : "",

        offeredWorkerUids:
          cleanAssignedUid
            ? []
            : (
                isDirect
                  ? cleanOffers
                  : []
              ),

        requestedWorkerUids:
          [],

        declinedWorkerUids:
          [],

        activated:
          false,

        tracking:
          false,

        arrived:
          false,

        activatedAt:
          null,

        trackingStartedAt:
          null,

        trackingEndedAt:
          null,

        arrivedAt:
          null,

        completedAt:
          null,

        noShowAt:
          null,

        reassignedFromUid:
          null,

        reassignReason:
          null,

        createdAt:
          serverTimestamp(),

        updatedAt:
          serverTimestamp(),
      });

  console.log(
    "CREATE SHIFT — SUCCESS",
    {
      shiftId:
        ref.id,
      tenantId:
        cleanTenantId,
      facilityId:
        cleanFacilityId,
    }
  );

  return ref.id;
}

/* =========================================================
   ASSIGN WORKER
   ========================================================= */

export async function assignWorkerToShift(
  shiftId,
  workerUid
) {
  return reassignWorkerOnShift(
    shiftId,
    workerUid,
    "manual_assignment"
  );
}

/* =========================================================
   REASSIGN
   ========================================================= */

export async function reassignWorkerOnShift(
  shiftId,
  workerUid,
  reason = "manual"
) {
  const cleanShiftId =
    normalizeUid(
      shiftId
    );

  const cleanWorkerUid =
    normalizeUid(
      workerUid
    );

  if (!cleanShiftId) {
    throw new Error(
      "Missing shiftId"
    );
  }

  /*
   * Null / empty workerUid means
   * unassign.
   */

  const shiftRef =
    doc(
      db,
      "shifts",
      cleanShiftId
    );

  return runTransaction(
    db,
    async (
      transaction
    ) => {
      const shiftSnap =
        await transaction.get(
          shiftRef
        );

      if (
        !shiftSnap.exists()
      ) {
        throw new Error(
          "Shift not found"
        );
      }

      const shift =
        shiftSnap.data();

      const previousUid =
        assignedUidFromShift(
          shift
        );

      let worker = null;

      if (
        cleanWorkerUid
      ) {
        const workerRef =
          doc(
            db,
            "users",
            cleanWorkerUid
          );

        const workerSnap =
          await transaction.get(
            workerRef
          );

        if (
          !workerSnap.exists()
        ) {
          throw new Error(
            "Worker not found"
          );
        }

        worker = {
          uid:
            workerSnap.id,
          ...workerSnap.data(),
        };

        if (
          !workerIsActive(
            worker
          )
        ) {
          throw new Error(
            "Worker is not active."
          );
        }

        if (
          !workerMatchesRole(
            worker,
            shift?.role
          )
        ) {
          throw new Error(
            `Worker does not match required role ${shift?.role || ""}.`
          );
        }
      }

      /*
       * Do overlap check outside the
       * transaction query system.
       *
       * This is intentionally performed
       * before update if a worker exists.
       */
      if (
        cleanWorkerUid
      ) {
        const startDate =
          toDate(
            shift?.startTime ||
              shift?.start
          );

        const endDate =
          toDate(
            shift?.endTime ||
              shift?.end
          );

     await assertNoOverlappingAssignedShift(
  cleanWorkerUid,
  startDate,
  endDate,
  cleanShiftId,
  shift?.tenantId,
  shift?.facilityId
);
      }

      const nextVersion =
        Number(
          shift
            ?.assignmentVersion ||
            0
        ) + 1;

      if (
        !cleanWorkerUid
      ) {
        transaction.update(
          shiftRef,
          {
            status:
              "open",

            lifecycleStatus:
              "open",

            mode:
              "pool",

            assignMode:
              "pool",

            assignedWorkerUid:
              null,

            assignedWorkerUids:
              [],

            assignedWorkerId:
              "",

            assignedWorkerName:
              "",

            assignedWorkerEmail:
              "",

            workerUid:
              "",

            workerId:
              "",

            workerName:
              "",

            assignedAt:
              null,

            assignedBy:
              currentUid(),

            assignmentReason:
              reason,

            assignmentVersion:
              nextVersion,

            reassignedFromUid:
              previousUid ||
              null,

            reassignReason:
              reason,

            assignmentAccepted:
              false,

            acceptedByWorker:
              false,

            acceptedWorkerUid:
              "",

            acceptedAt:
              null,

            workerResponseStatus:
              "",

            offeredWorkerUids:
              [],

            requestedWorkerUids:
              [],

            activated:
              false,

            tracking:
              false,

            arrived:
              false,

            activatedAt:
              null,

            trackingStartedAt:
              null,

            trackingEndedAt:
              null,

            arrivedAt:
              null,

            updatedAt:
              serverTimestamp(),
          }
        );

        return true;
      }

      const workerName =
        normalizeText(
          worker?.name ||
            worker?.displayName ||
            `${worker?.firstName || ""} ${worker?.lastName || ""}` ||
            worker?.email ||
            ""
        );

      transaction.update(
        shiftRef,
        {
          status:
            "assigned",

          lifecycleStatus:
            "assigned",

          assignedWorkerUid:
            cleanWorkerUid,

          assignedWorkerUids:
            [
              cleanWorkerUid,
            ],

          assignedWorkerId:
            cleanWorkerUid,

          assignedWorkerName:
            workerName,

          assignedWorkerEmail:
            normalizeEmail(
              worker?.email
            ),

          workerUid:
            cleanWorkerUid,

          workerId:
            cleanWorkerUid,

          workerName,

          assignedAt:
            serverTimestamp(),

          assignedBy:
            currentUid(),

          assignmentReason:
            reason,

          assignmentVersion:
            nextVersion,

          reassignedFromUid:
            previousUid ||
            null,

          reassignReason:
            reason,

          assignmentAccepted:
            false,

          acceptedByWorker:
            false,

          acceptedWorkerUid:
            "",

          acceptedAt:
            null,

          workerResponseStatus:
            "pending",

          offeredWorkerUids:
            [],

          requestedWorkerUids:
            [],

          activated:
            false,

          tracking:
            false,

          arrived:
            false,

          activatedAt:
            null,

          trackingStartedAt:
            null,

          trackingEndedAt:
            null,

          arrivedAt:
            null,

          updatedAt:
            serverTimestamp(),
        }
      );

      return true;
    }
  );
}

/* =========================================================
   WORKER REQUESTS POOL SHIFT
   ========================================================= */

export async function requestPoolShift(
  shiftId,
  workerUid
) {
  const uid =
    normalizeUid(
      workerUid ||
        currentUid()
    );

  if (!uid) {
    throw new Error(
      "Missing worker UID"
    );
  }

  const {
    ref,
    data,
  } =
    await getShiftOrThrow(
      shiftId
    );

  const status =
    normalizeId(
      data?.status
    );

  if (
    ![
      "open",
      "time_required",
    ].includes(status)
  ) {
    throw new Error(
      "Shift is not open for requests."
    );
  }

  const mode =
    normalizeId(
      data?.mode ||
        data?.assignMode
    );

  if (
    mode !== "pool"
  ) {
    throw new Error(
      "Shift is not a pool shift."
    );
  }

  const existing =
    Array.isArray(
      data
        ?.requestedWorkerUids
    )
      ? data
          .requestedWorkerUids
      : [];

  if (
    existing.includes(uid)
  ) {
    return true;
  }

  await updateDoc(
    ref,
    {
      requestedWorkerUids:
        arrayUnion(uid),

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   PLANNER DECLINES POOL REQUEST
   ========================================================= */

export async function declineShift(
  shiftId,
  workerUid
) {
  const uid =
    normalizeUid(
      workerUid
    );

  if (!uid) {
    throw new Error(
      "Missing worker UID"
    );
  }

  const {
    ref,
    data,
  } =
    await getShiftOrThrow(
      shiftId
    );

  const requests =
    Array.isArray(
      data
        ?.requestedWorkerUids
    )
      ? data
          .requestedWorkerUids
      : [];

  const declined =
    Array.isArray(
      data
        ?.declinedWorkerUids
    )
      ? data
          .declinedWorkerUids
      : [];

  if (
    !requests.includes(uid)
  ) {
    return true;
  }

  await updateDoc(
    ref,
    {
      requestedWorkerUids:
        requests.filter(
          (
            item
          ) =>
            item !== uid
        ),

      declinedWorkerUids:
        [
          ...new Set([
            ...declined,
            uid,
          ]),
        ],

      lastRequestDeclinedAt:
        serverTimestamp(),

      lastRequestDeclinedBy:
        currentUid(),

      lastDeclinedWorkerUid:
        uid,

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   APPROVE POOL REQUEST
   ========================================================= */

export async function approvePoolRequest(
  shiftId,
  workerUid,
  reason = "pool_approved"
) {
  return reassignWorkerOnShift(
    shiftId,
    workerUid,
    reason
  );
}

/* =========================================================
   WORKER ACCEPTS PLANNER ASSIGNMENT
   ========================================================= */

export async function acceptPlannerAssignment(
  shiftId,
  workerUid
) {
  const uid =
    normalizeUid(
      workerUid ||
        currentUid()
    );

  const {
    ref,
    data,
  } =
    await getShiftOrThrow(
      shiftId
    );

  const assignedUid =
    assignedUidFromShift(
      data
    );

  if (
    assignedUid !== uid
  ) {
    throw new Error(
      "Shift is not assigned to this worker."
    );
  }

  await updateDoc(
    ref,
    {
      status:
        "assigned",

      lifecycleStatus:
        "assigned",

      assignedWorkerUid:
        uid,

      assignedWorkerUids:
        [uid],

      assignedWorkerId:
        uid,

      workerUid:
        uid,

      workerId:
        uid,

      assignmentAccepted:
        true,

      acceptedByWorker:
        true,

      acceptedWorkerUid:
        uid,

      acceptedAt:
        serverTimestamp(),

      workerResponseStatus:
        "accepted",

      activated:
        false,

      tracking:
        false,

      arrived:
        false,

      activatedAt:
        null,

      trackingStartedAt:
        null,

      trackingEndedAt:
        null,

      arrivedAt:
        null,

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   WORKER DECLINES PLANNER ASSIGNMENT
   ========================================================= */

export async function declinePlannerAssignment(
  shiftId,
  workerUid,
  reason = "Unable to work this shift"
) {
  const uid =
    normalizeUid(
      workerUid ||
        currentUid()
    );

  if (!uid) {
    throw new Error(
      "Missing worker UID."
    );
  }

  const {
    ref,
    data,
  } =
    await getShiftOrThrow(
      shiftId
    );

  const assignedUid =
    assignedUidFromShift(
      data
    );

  if (
    assignedUid !== uid
  ) {
    throw new Error(
      "Shift is not assigned to this worker."
    );
  }

  const status =
    normalizeText(
      data?.status ||
        data?.lifecycleStatus ||
        ""
    ).toLowerCase();

  if (status !== "assigned") {
    throw new Error(
      "This shift is no longer awaiting worker response."
    );
  }

  const declined =
    Array.isArray(
      data?.declinedWorkerUids
    )
      ? data.declinedWorkerUids
      : [];

  await updateDoc(
    ref,
    {
      /*
       * Return declined planner assignment
       * to the available pool.
       */
      status:
        "open",

      lifecycleStatus:
        "open",

      mode:
        "pool",

      assignMode:
        "pool",

      /*
       * Clear canonical assignment identity.
       */
      assignedWorkerUid:
        null,

      assignedWorkerUids:
        [],

      assignedWorkerId:
        "",

      assignedWorkerName:
        "",

      assignedWorkerEmail:
        "",

      workerUid:
        "",

      workerId:
        "",

      workerName:
        "",

      /*
       * Clear assignment state.
       */
      assignedAt:
        null,

      assignedBy:
        null,

      assignmentReason:
        null,

      /*
       * Clear acceptance evidence.
       */
      assignmentAccepted:
        false,

      acceptedByWorker:
        false,

      acceptedWorkerUid:
        "",

      acceptedAt:
        null,

      workerResponseStatus:
        "declined",

      /*
       * Preserve decline history.
       */
      declinedWorkerUids:
        [
          ...new Set([
            ...declined,
            uid,
          ]),
        ],

      declinedAt:
        serverTimestamp(),

      declinedByUid:
        uid,

      declineReason:
        normalizeText(reason) ||
        "Unable to work this shift",

      /*
       * Clear previous pool/direct
       * interaction state.
       */
      requestedWorkerUids:
        [],

      requestedByUid:
        null,

      requestedAt:
        null,

      offeredWorkerUids:
        [],

      /*
       * Reset active lifecycle state.
       */
      activated:
        false,

      tracking:
        false,

      arrived:
        false,

      activatedAt:
        null,

      activatedByUid:
        null,

      trackingStartedAt:
        null,

      trackingByUid:
        null,

      trackingEndedAt:
        null,

      arrivedAt:
        null,

      arrivedByUid:
        null,

      arrivalConfirmedAt:
        null,

      arrivalMethod:
        null,

      arrivalDistanceMeters:
        null,

      arrivalAccuracyMeters:
        null,

      /*
       * Assignment audit trail.
       */
      assignmentVersion:
        Number(
          data?.assignmentVersion ||
            0
        ) + 1,

      reassignedFromUid:
        uid,

      reassignReason:
        "worker_declined_assignment",

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   WORKER ACCEPTS DIRECT OFFER
   ========================================================= */

export async function acceptShiftOffer(
  shiftId,
  workerUid
) {
  const uid =
    normalizeUid(
      workerUid ||
        currentUid()
    );

  const {
    ref,
    data,
  } =
    await getShiftOrThrow(
      shiftId
    );

  const offers =
    Array.isArray(
      data
        ?.offeredWorkerUids
    )
      ? data
          .offeredWorkerUids
      : [];

  if (
    !offers.includes(uid)
  ) {
    throw new Error(
      "Worker does not have this direct offer."
    );
  }

 await assertNoOverlappingAssignedShift(
  uid,
  data?.startTime ||
    data?.start,
  data?.endTime ||
    data?.end,
  shiftId,
  data?.tenantId
);

  await updateDoc(
    ref,
    {
      status:
        "assigned",

      lifecycleStatus:
        "assigned",

      assignedWorkerUid:
        uid,

      assignedWorkerUids:
        [uid],

      assignedWorkerId:
        uid,

      workerUid:
        uid,

      workerId:
        uid,

      assignmentAccepted:
        true,

      acceptedByWorker:
        true,

      acceptedWorkerUid:
        uid,

      acceptedAt:
        serverTimestamp(),

      workerResponseStatus:
        "accepted",

      assignedAt:
        serverTimestamp(),

      assignedBy:
        uid,

      assignmentReason:
        "direct_offer_accepted",

      assignmentVersion:
        Number(
          data
            ?.assignmentVersion ||
            0
        ) + 1,

      offeredWorkerUids:
        [],

      requestedWorkerUids:
        [],

      activated:
        false,

      tracking:
        false,

      arrived:
        false,

      activatedAt:
        null,

      trackingStartedAt:
        null,

      trackingEndedAt:
        null,

      arrivedAt:
        null,

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   WORKER DECLINES DIRECT OFFER
   ========================================================= */

export async function declineShiftOffer(
  shiftId,
  workerUid,
  fallbackToPool = true
) {
  const uid =
    normalizeUid(
      workerUid ||
        currentUid()
    );

  const {
    ref,
    data,
  } =
    await getShiftOrThrow(
      shiftId
    );

  const offers =
    Array.isArray(
      data
        ?.offeredWorkerUids
    )
      ? data
          .offeredWorkerUids
      : [];

  const nextOffers =
    offers.filter(
      (
        item
      ) =>
        item !== uid
    );

  const declined =
    Array.isArray(
      data
        ?.declinedWorkerUids
    )
      ? data
          .declinedWorkerUids
      : [];

  const shouldReturnToPool =
    fallbackToPool &&
    nextOffers.length === 0;

  await updateDoc(
    ref,
    {
      declinedWorkerUids:
        [
          ...new Set([
            ...declined,
            uid,
          ]),
        ],

      offeredWorkerUids:
        nextOffers,

      mode:
        shouldReturnToPool
          ? "pool"
          : "direct",

      assignMode:
        shouldReturnToPool
          ? "pool"
          : "direct",

      status:
        shouldReturnToPool
          ? "open"
          : "offered",

      lifecycleStatus:
        shouldReturnToPool
          ? "open"
          : "offered",

      workerResponseStatus:
        "declined",

      declinedAt:
        serverTimestamp(),

      declinedByUid:
        uid,

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   UPDATE SHIFT TIME
   ========================================================= */

export async function updateShiftTime(
  shiftId,
  startTime,
  endTime
) {
  const start =
    toDate(
      startTime
    );

  const end =
    toDate(
      endTime
    );

  if (
    !start ||
    !end
  ) {
    throw new Error(
      "Invalid shift time."
    );
  }

  if (
    end.getTime() <=
    start.getTime()
  ) {
    throw new Error(
      "Shift end time must be after start time."
    );
  }

  const {
    ref,
  } =
    await getShiftOrThrow(
      shiftId
    );

  await updateDoc(
    ref,
    {
      startTime:
        Timestamp.fromDate(
          start
        ),

      endTime:
        Timestamp.fromDate(
          end
        ),

      start:
        Timestamp.fromDate(
          start
        ),

      end:
        Timestamp.fromDate(
          end
        ),

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   CONFIRM FLEXIBLE SHIFT TIME
   ========================================================= */

export async function confirmShiftTime({
  shiftId,
  startTime,
  endTime,
  actorUid,
  timeSlot = "Custom",
  assignedWorkerUid = "",
}) {
  const start =
    toDate(
      startTime
    );

  const end =
    toDate(
      endTime
    );

  if (
    !start ||
    !end
  ) {
    throw new Error(
      "Invalid shift time."
    );
  }

  const {
    ref,
  } =
    await getShiftOrThrow(
      shiftId
    );

  const workerUid =
    normalizeUid(
      assignedWorkerUid
    );

  await updateDoc(
    ref,
    {
      startTime:
        Timestamp.fromDate(
          start
        ),

      endTime:
        Timestamp.fromDate(
          end
        ),

      start:
        Timestamp.fromDate(
          start
        ),

      end:
        Timestamp.fromDate(
          end
        ),

      timeSlot,

      timeRequired:
        false,

      timeConfirmed:
        true,

      isFlexibleTime:
        false,

      status:
        workerUid
          ? "assigned"
          : "open",

      lifecycleStatus:
        workerUid
          ? "assigned"
          : "open",

      assignedWorkerUid:
        workerUid ||
        null,

      assignedWorkerUids:
        workerUid
          ? [workerUid]
          : [],

      assignedWorkerId:
        workerUid,

      workerUid:
        workerUid,

      workerId:
        workerUid,

      assignedAt:
        workerUid
          ? serverTimestamp()
          : null,

      assignedBy:
        normalizeUid(
          actorUid ||
            currentUid()
        ),

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   REMOVE SHIFT
   ========================================================= */

export async function removeShift(
  shiftId
) {
  const id =
    normalizeUid(
      shiftId
    );

  if (!id) {
    throw new Error(
      "Missing shiftId"
    );
  }

  /*
   * Firestore rules determine whether
   * the authenticated user actually has
   * canDeleteShifts.
   *
   * Facility Planners normally do NOT.
   */

  await deleteDoc(
    doc(
      db,
      "shifts",
      id
    )
  );

  return true;
}

/* =========================================================
   CLOSE UNFILLED
   ========================================================= */

export async function closeShiftUnfilled(
  shiftId,
  actorUid,
  reason = "closed_unfilled"
) {
  const {
    ref,
  } =
    await getShiftOrThrow(
      shiftId
    );

  await updateDoc(
    ref,
    {
      status:
        "closed_unfilled",

      lifecycleStatus:
        "closed_unfilled",

      closedAt:
        serverTimestamp(),

      closedReason:
        normalizeText(
          reason
        ),

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   ACTIVATE
   ========================================================= */

export async function setShiftActivated(
  shiftId,
  workerUid,
  activationWindowHours = 2
) {
  const uid =
    normalizeUid(
      workerUid ||
        currentUid()
    );

  const {
    ref,
    data,
  } =
    await getShiftOrThrow(
      shiftId
    );

  assertShiftActivation({
    shift: data,
    workerUid: uid,
    nowMs: Date.now(),
    activationWindowHours,
  });

  await updateDoc(
    ref,
    {
      status:
        "activated",

      lifecycleStatus:
        "activated",

      activated:
        true,

      activatedAt:
        serverTimestamp(),

      activatedByUid:
        uid,

      activatedLate:
        false,

      activatedLateMinutes:
        0,

      arrived:
        false,

      arrivedAt:
        null,

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   TRACKING
   ========================================================= */

export async function setShiftTracking(
  shiftId,
  workerUid
) {
  const uid =
    normalizeUid(
      workerUid ||
        currentUid()
    );

  const {
    ref,
    data,
  } =
    await getShiftOrThrow(
      shiftId
    );

  if (
    assignedUidFromShift(
      data
    ) !== uid
  ) {
    throw new Error(
      "Shift is not assigned to this worker."
    );
  }

  const currentStatus =
    normalizeId(
      data?.lifecycleStatus ||
        data?.status ||
        ""
    );

  if (
    currentStatus !== "activated"
  ) {
    throw new Error(
      "Shift must be activated before marking On My Way."
    );
  }

  await updateDoc(
    ref,
    {
      status:
        "tracking",

      lifecycleStatus:
        "tracking",

      tracking:
        true,

      trackingStartedAt:
        serverTimestamp(),

      trackingByUid:
        uid,

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   ARRIVAL
   ========================================================= */

export async function setShiftArrived(
  shiftId,
  workerUid,
  arrivalMeta = {}
) {
  const uid =
    normalizeUid(
      workerUid ||
        currentUid()
    );

  const {
    ref,
    data,
  } =
    await getShiftOrThrow(
      shiftId
    );

   if (
    assignedUidFromShift(
      data
    ) !== uid
  ) {
    throw new Error(
      "Shift is not assigned to this worker."
    );
  }

  const currentStatus =
    normalizeId(
      data?.lifecycleStatus ||
        data?.status ||
        ""
    );

  if (
    currentStatus !== "tracking"
  ) {
    throw new Error(
      "Worker must mark I'm On My Way before arrival."
    );
  }

  await updateDoc(
    ref,
    {
      status:
        "arrived",

      lifecycleStatus:
        "arrived",

      arrived:
        true,

      arrivedAt:
        serverTimestamp(),

      arrivalConfirmedAt:
        serverTimestamp(),

      arrivedByUid:
        uid,

      arrivalMethod:
        normalizeText(
          arrivalMeta
            ?.method ||
            arrivalMeta
              ?.arrivalMethod ||
            "manual"
        ),

      arrivalDistanceMeters:
        Number(
          arrivalMeta
            ?.distanceMeters ??
            arrivalMeta
              ?.arrivalDistanceMeters ??
            0
        ),

      arrivalAccuracyMeters:
        Number(
          arrivalMeta
            ?.accuracyMeters ??
            arrivalMeta
              ?.arrivalAccuracyMeters ??
            0
        ),

      tracking:
        false,

      trackingEndedAt:
        serverTimestamp(),

      riskLevel:
        normalizeId(
          arrivalMeta
            ?.riskLevel ||
            "normal"
        ),

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   COMPLETE
   ========================================================= */

export async function setShiftCompleted(
  shiftId,
  workerUid,
  actorUid = null
) {
  const {
    ref,
    data,
  } =
    await getShiftOrThrow(
      shiftId
    );

  const assignedUid =
    assignedUidFromShift(
      data
    );

  if (!assignedUid) {
    throw new Error(
      "Shift has no assigned worker."
    );
  }

  const targetWorkerUid =
    normalizeUid(
      workerUid ||
        assignedUid
    );

  if (
    targetWorkerUid !==
    assignedUid
  ) {
    throw new Error(
      "Completion worker does not match the current shift assignment."
    );
  }

  /*
   * actorUid identifies who actually performed
   * the completion action.
   *
   * Worker:
   *   actorUid defaults to current authenticated
   *   worker.
   *
   * Planner/Admin:
   *   caller supplies its own authenticated UID.
   */
  const completionActorUid =
    normalizeUid(
      actorUid ||
        currentUid()
    );

  if (!completionActorUid) {
    throw new Error(
      "Missing completion actor UID."
    );
  }

  const currentStatus =
    normalizeId(
      data?.lifecycleStatus ||
        data?.status ||
        ""
    );

  /*
   * A worker must complete the operational
   * visibility chain before completing the shift.
   *
   * Planner/Admin completion remains an
   * authorized operational override.
   */
   if (
    completionActorUid ===
      assignedUid &&
    currentStatus !== "arrived"
  ) {
    throw new Error(
      "Worker must arrive before completing the shift."
    );
  }

  const scheduledEndMs =
    typeof data?.endTime?.toMillis === "function"
      ? data.endTime.toMillis()
      : data?.endTime instanceof Date
        ? data.endTime.getTime()
        : typeof data?.endTime === "number"
          ? data.endTime
          : Date.parse(data?.endTime || "");

  if (!Number.isFinite(scheduledEndMs)) {
    throw new Error(
      "Shift has no valid scheduled end time."
    );
  }

  if (Date.now() < scheduledEndMs) {
    throw new Error(
      "Shift cannot be completed before its scheduled end time."
    );
  }

  await updateDoc(
    ref,
    {
      status:
        "completed",

      lifecycleStatus:
        "completed",

      completedAt:
        serverTimestamp(),

      completedByUid:
        completionActorUid,

      invoiceStatus:
        "pending",

      ratingStatus:
        "pending",

      tracking:
        false,

      trackingEndedAt:
        serverTimestamp(),

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}

/* =========================================================
   NO SHOW
   ========================================================= */

export async function setShiftNoShow(
  shiftId,
  plannerUid,
  reason = "Marked no-show"
) {
  const {
    ref,
  } =
    await getShiftOrThrow(
      shiftId
    );

  await updateDoc(
    ref,
    {
      status:
        "no_show",

      lifecycleStatus:
        "no_show",

      noShowAt:
        serverTimestamp(),

      noShowByUid:
        normalizeUid(
          plannerUid ||
            currentUid()
        ),

      noShowReason:
        normalizeText(
          reason
        ),

      riskLevel:
        "high",

      tracking:
        false,

      trackingEndedAt:
        serverTimestamp(),

      updatedAt:
        serverTimestamp(),
    }
  );

  return true;
}
// Worker withdrawal changes only this worker's request membership.
export async function withdrawPoolRequest(shiftId, workerUid) {
  const uid = normalizeUid(workerUid || currentUid());
  if (!uid || uid !== currentUid()) throw new Error("Withdrawal must be performed by the requesting worker.");
  const { ref, data } = await getShiftOrThrow(shiftId);
  if (!(data.requestedWorkerUids || []).includes(uid)) return true;
  await updateDoc(ref, {
    requestedWorkerUids: arrayRemove(uid),
    updatedAt: serverTimestamp(),
  });
  return true;
}

// Planner removal preserves shift history through the existing cancellation contract.
export async function cancelShift(shiftId, actorUid, reason = "planner_cancel") {
  const uid = normalizeUid(actorUid || currentUid());
  if (!uid || uid !== currentUid()) throw new Error("Cancellation actor must match the signed-in user.");
  const { ref, data } = await getShiftOrThrow(shiftId);
  const status = normalizeId(data.lifecycleStatus || data.status);
  if (["cancelled", "canceled"].includes(status)) return { action: "cancelled" };
  if (["completed", "no_show", "closed_unfilled"].includes(status)) {
    throw new Error("Terminal shift history cannot be cancelled.");
  }
  await updateDoc(ref, {
    status: "cancelled", lifecycleStatus: "cancelled",
    cancelledAt: serverTimestamp(), cancelledByUid: uid,
    cancelReason: normalizeText(reason), tracking: false,
    trackingEndedAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  return { action: "cancelled" };
}
